/**
 * 私聊消息处理模块
 *
 * 职责：
 * 1. 接收和转换私聊消息（PUSH_MSG）
 * 2. 批量保存消息到数据库（使用节流优化）
 * 3. 更新会话列表和聊天页面
 *
 * 优化策略：
 * - 数据库批量写入：使用 100ms 节流，避免频繁 IO 操作
 * - 会话列表更新：使用 100ms 节流，避免频繁 UI 更新
 * - 聊天页面消息：实时显示，不节流（用户正在查看）
 */

import { useConversationStore } from '@/stores/conversation'
import { useChatStore } from '@/stores/chat'
import { useUserStore } from '@/stores/user'
import { messageSyncManager } from '@/managers/MessageSyncManager'
// import { dbManager } from '@/indexdb/chat'
import { IMContentEncrypt } from '@/utils/crypto'
import { showToast } from 'vant'
import { omit, throttle } from 'lodash'
import { t } from '@/locales'
import { PRIVATE_MSG_TYPES, SUPPORTED_PRIVATE_MSG_TYPES } from '@/common/constants/msgTypes'

// ========== 私聊消息队列 ==========
let privateMessageQueue = []

// ========== 节流函数 ==========

/**
 * 节流版本的数据库批量保存函数
 * 100ms 内的多次调用会被合并成一次数据库操作
 */
const throttledBatchSave = throttle(
  async () => {
    if (privateMessageQueue.length === 0) return

    // 取出所有待处理消息
    const messagesToSave = [...privateMessageQueue]
    privateMessageQueue = []

    console.log(`[PrivateMessages] Batch saving ${messagesToSave.length} messages to DB`)

    try {
      // 批量保存到数据库
      const { conversationIds } = await messageSyncManager.saveMessagesBatch(messagesToSave)

      // 更新会话列表
      updateConversations()

      return { conversationIds, messages: messagesToSave }
    } catch (error) {
      console.error('[PrivateMessages] Failed to batch save messages:', error)
      showToast(t('chat.error.messageSaveFailed'))
      return { conversationIds: [], messages: [] }
    }
  },
  100,
  {
    leading: true,  // 首次立即执行
    trailing: true  // 结束后再执行一次
  }
)

/**
 * 会话列表更新函数
 */
const updateConversations = () => {
  const conversationStore = useConversationStore()
  conversationStore.initConversations()
}
// ========== 工具函数 ==========

/**
 * 根据消息类型生成消息预览
 * 如果是不支持的消息类型，返回"该消息类型暂未支持"的文案
 * @param {Object} payload - 消息负载
 * @returns {string} 最终的消息预览
 */
function generateMessagePreview(payload) {
  const { msg_preview, sub_type, content, send_uid } = payload

  if (!SUPPORTED_PRIVATE_MSG_TYPES.includes(sub_type)) {
    // 如果是不支持的消息类型，返回固定文案
    return t('chat.message.unsupported')
  }
  if (sub_type === PRIVATE_MSG_TYPES.HINT_VIDEO) {
    // 服务端推送的 HintVideo 消息，msg_preview 没有区分发起方还是接收方，所以需要根据 send_uid 判断，特殊处理
    const { member_content, target_content } = JSON.parse(content)
    const userStore = useUserStore()
    const id = userStore.getId()
    return send_uid === id ? member_content : target_content
  }
  // 如果是支持的消息类型，使用原始预览
  return msg_preview
}

/**
 * 将 IMManager 派发的原始 content 转换为 MsgBean 格式
 * @param {Object[]} contents - 原始消息数据数组
 * @returns {Object[]} MsgBean 格式的消息数组
 */
export function transformToMsgBeans(contents = []) {
  return contents.map(content => {
    let { msg_content, push_msg } = content
    push_msg = push_msg || msg_content?.push_msg || {}

    const { meta_type, sub_type } = push_msg
    return {
      type: meta_type || sub_type,
      payload: {
        ...omit(content, ['msg_content', 'push_msg']), // 过滤掉 msg_content 和 push_msg 字段
        ...push_msg,
      }
    }
  })
}

/**
 * 过滤掉数据库中已存在的消息
 * @param {Object[]} msgBeans - MsgBean 格式的消息数组
 * @returns {Promise<Object[]>} 过滤后的消息数组
 */
// async function filterExistingMessages(msgBeans) {
//   if (msgBeans.length === 0) return []

//   const db = dbManager.getDatabase()
//   if (!db) {
//     return msgBeans
//   }

//   try {
//     // 提取所有 msg_id
//     const msgIds = msgBeans.map(bean => bean.payload.msg_id).filter(Boolean)

//     if (msgIds.length === 0) return msgBeans

//     // 批量查询数据库中已存在的消息
//     const existingMessages = await db.messages.where('msg_id').anyOf(msgIds).toArray()
//     const existingMsgIds = new Set(existingMessages.map(msg => msg.msg_id))

//     // 过滤掉已存在的消息
//     const newMessages = msgBeans.filter(bean => !existingMsgIds.has(bean.payload.msg_id))

//     return newMessages
//   } catch (error) {
//     // 如果过滤失败，返回原始数据（保守处理）
//     return msgBeans
//   }
// }

/**
 * 提取数据库需要的字段
 * @param {Object[]} msgBeans - MsgBean 格式的消息数组
 * @returns {Object[]} 数据库消息数组
 */
function extractDbMessages(msgBeans) {
  return msgBeans.map(({ payload }) => {
    const { content, conversation_id, created_at, msg_id, msg_source, send_uid, seq_id, sub_type } = payload
    const createdAt = Number(created_at)
    const encryptedContent = IMContentEncrypt(content)

    // 🔧 根据消息类型生成预览：只有支持的消息类型才使用原始预览，否则显示"暂未支持"
    const finalPreview = generateMessagePreview(payload)
    const encryptedMsgPreview = IMContentEncrypt(finalPreview)

    return {
      conversation_id,
      created_at: createdAt,
      msg_id,
      msg_source,
      send_uid,
      seq_id,
      sub_type,
      content: encryptedContent,
      msg_preview: encryptedMsgPreview,
    }
  })
}

/**
 * 实时更新聊天页面消息（不节流）
 * @param {Object[]} dbMessages - 数据库消息数组
 */
function updateChatPageRealtime(dbMessages) {
  const chatStore = useChatStore()
  const currentConversationId = chatStore.currentConversationId

  // 只更新当前正在查看的会话
  if (!currentConversationId) {
    return
  }

  // 过滤出当前会话的消息
  const relevantMessages = dbMessages.filter(
    msg => msg.conversation_id === currentConversationId
  )

  if (relevantMessages.length > 0) {
    // ✅ 聊天页面实时显示消息，不节流
    chatStore.receiveMessages(relevantMessages)
    console.log(`[PrivateMessages] Real-time updated ${relevantMessages.length} messages in chat page`)
  }
}

// ========== 主处理函数 ==========

/**
 * 处理私聊消息
 *
 * 处理流程：
 * 1. 转换为 MsgBean 格式
 * 2. 过滤掉已存在的消息（去重）
 * 3. 提取数据库需要的字段
 * 4. 批量保存到数据库（使用节流）
 * 5. 实时更新聊天页面
 *
 * 优化策略：
 * 1. 数据库操作使用节流（100ms）- 避免频繁 IO
 * 2. 会话列表更新使用节流（100ms）- 避免频繁 UI 更新
 * 3. 聊天页面消息实时显示 - 用户正在查看，需要即时反馈
 *
 * @param {Object} event - EventBus 事件对象
 * @param {Object[]} event.data - IMManager 派发的原始消息数组
 */
export async function handlePrivateMessages(event) {
  console.log('[handlePrivateMessages] event:', event)
  const { data: rawMessages } = event
  if (!Array.isArray(rawMessages) || rawMessages.length === 0) return

  try {
    // 1. 转换为 MsgBean 格式
    const msgBeans = transformToMsgBeans(rawMessages)

    // 2. 过滤掉已存在的消息
    // msgBeans = await filterExistingMessages(msgBeans)

    // 如果没有新消息，直接返回
    // if (msgBeans.length === 0) {
      // 也更新一下会话列表，因为发送消息的时候会自动插入 msgBean 到 db，这时候 filterExistingMessages 会过滤掉
      // 导致 msgBeans.length === 0
      // updateConversations()
      // return
    // }

    // 3. 提取数据库需要的字段
    const dbMessages = extractDbMessages(msgBeans)

    // 4. 加入消息队列（节流保存）
    privateMessageQueue.push(...dbMessages)

    // 5. 触发节流保存（100ms 内的多次调用会被合并）
    throttledBatchSave()

    // 6. 如果在聊天页面，立即显示消息（不节流）
    updateChatPageRealtime(dbMessages)

  } catch (error) {
    showToast(t('chat.error.messageHandleFailed'))
  }
}
